/**
 * Pure shape geometry.
 *
 * Every node outline is produced here as SVG path data in node-local
 * coordinates (origin at the node's top-left). Both the on-screen React
 * renderer and the headless SVG exporter call these functions, which is why
 * they take plain numbers and return plain strings — no DOM, no React, no
 * theme. That shared origin is what makes an exported file match the screen.
 */
import type { NodeShape, Rect, Size } from '../graph/model/types';

export interface ShapeGeometry {
  /** Path data for the filled, stroked outline. */
  readonly outline: string;
  /**
   * Extra strokes drawn over the fill — the twin bars of a subprocess, the
   * folded corner of a note. Never filled.
   */
  readonly details: readonly string[];
  /** The rectangle the label is centred in. */
  readonly textBox: Rect;
}

/** Corner radius for rectangular shapes. */
const RADIUS = 8;
/** Inset of a subprocess's vertical bars from its edges. */
const SUBPROCESS_BAR = 9;
/** Horizontal slant of a parallelogram. */
const IO_SLANT = 16;
/** Size of a note's folded corner. */
const NOTE_FOLD = 14;

function roundedRect(width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  if (r === 0) return `M0 0H${width}V${height}H0Z`;
  return [
    `M${r} 0`,
    `H${width - r}`,
    `A${r} ${r} 0 0 1 ${width} ${r}`,
    `V${height - r}`,
    `A${r} ${r} 0 0 1 ${width - r} ${height}`,
    `H${r}`,
    `A${r} ${r} 0 0 1 0 ${height - r}`,
    `V${r}`,
    `A${r} ${r} 0 0 1 ${r} 0`,
    'Z',
  ].join(' ');
}

export function shapeGeometry(shape: NodeShape, size: Size): ShapeGeometry {
  const { width: w, height: h } = size;

  switch (shape) {
    case 'terminator': {
      // A stadium: fully rounded ends, so start and end read as "outside" the
      // process boxes at a glance.
      const r = h / 2;
      return {
        outline: roundedRect(w, h, r),
        details: [],
        textBox: { x: r * 0.6, y: 0, width: Math.max(0, w - r * 1.2), height: h },
      };
    }

    case 'decision': {
      const mx = w / 2;
      const my = h / 2;
      return {
        outline: `M${mx} 0 L${w} ${my} L${mx} ${h} L0 ${my} Z`,
        details: [],
        // Text lives in the inscribed rectangle, roughly half the diamond.
        textBox: { x: w * 0.2, y: h * 0.25, width: w * 0.6, height: h * 0.5 },
      };
    }

    case 'subprocess': {
      return {
        outline: roundedRect(w, h, RADIUS),
        details: [`M${SUBPROCESS_BAR} 0V${h}`, `M${w - SUBPROCESS_BAR} 0V${h}`],
        textBox: {
          x: SUBPROCESS_BAR + 4,
          y: 0,
          width: Math.max(0, w - (SUBPROCESS_BAR + 4) * 2),
          height: h,
        },
      };
    }

    case 'io': {
      const slant = Math.min(IO_SLANT, w / 3);
      return {
        outline: `M${slant} 0 H${w} L${w - slant} ${h} H0 Z`,
        details: [],
        textBox: { x: slant, y: 0, width: Math.max(0, w - slant * 2), height: h },
      };
    }

    case 'note': {
      const fold = Math.min(NOTE_FOLD, w / 4, h / 2);
      return {
        outline: `M0 0 H${w - fold} L${w} ${fold} V${h} H0 Z`,
        details: [`M${w - fold} 0 V${fold} H${w}`],
        textBox: { x: 6, y: 2, width: Math.max(0, w - fold - 8), height: h - 4 },
      };
    }

    case 'junction': {
      const r = Math.min(w, h) / 2;
      return {
        outline: `M${w / 2 - r} ${h / 2} a${r} ${r} 0 1 0 ${r * 2} 0 a${r} ${r} 0 1 0 ${-r * 2} 0`,
        details: [],
        textBox: { x: 0, y: 0, width: w, height: h },
      };
    }

    case 'error':
    case 'process':
      return {
        outline: roundedRect(w, h, RADIUS),
        details: [],
        textBox: { x: 0, y: 0, width: w, height: h },
      };
  }
}

/**
 * Arrowhead path for an edge arriving at `point` from `direction`.
 * Drawn as a filled triangle so it reads clearly at small zoom levels.
 */
export function arrowHead(
  point: { x: number; y: number },
  direction: 'up' | 'down' | 'left' | 'right',
  size = 8,
): string {
  const { x, y } = point;
  const w = size * 0.62;
  switch (direction) {
    case 'down':
      return `M${x} ${y} L${x - w} ${y - size} L${x + w} ${y - size} Z`;
    case 'up':
      return `M${x} ${y} L${x - w} ${y + size} L${x + w} ${y + size} Z`;
    case 'right':
      return `M${x} ${y} L${x - size} ${y - w} L${x - size} ${y + w} Z`;
    case 'left':
      return `M${x} ${y} L${x + size} ${y - w} L${x + size} ${y + w} Z`;
  }
}

/** Direction the last segment of a polyline travels in. */
export function terminalDirection(
  points: readonly { x: number; y: number }[],
): 'up' | 'down' | 'left' | 'right' {
  if (points.length < 2) return 'down';
  const last = points[points.length - 1]!;
  const previous = points[points.length - 2]!;
  if (Math.abs(last.x - previous.x) > Math.abs(last.y - previous.y)) {
    return last.x >= previous.x ? 'right' : 'left';
  }
  return last.y >= previous.y ? 'down' : 'up';
}

/**
 * Pulls the polyline's endpoint back by `inset` so the line stops where the
 * arrowhead begins instead of poking through it.
 */
export function trimForArrow(
  points: readonly { x: number; y: number }[],
  inset: number,
): { x: number; y: number }[] {
  if (points.length < 2 || inset <= 0) return [...points];
  const trimmed = points.map((p) => ({ ...p }));
  const last = trimmed[trimmed.length - 1]!;
  const previous = trimmed[trimmed.length - 2]!;

  if (last.x === previous.x) {
    const span = Math.abs(last.y - previous.y);
    if (span <= inset) return trimmed;
    last.y += last.y > previous.y ? -inset : inset;
  } else if (last.y === previous.y) {
    const span = Math.abs(last.x - previous.x);
    if (span <= inset) return trimmed;
    last.x += last.x > previous.x ? -inset : inset;
  }
  return trimmed;
}
